import { Bars2Icon, Bars3Icon, ChatBubbleLeftIcon, CheckIcon, CircleStackIcon, CpuChipIcon, ExclamationTriangleIcon, EyeIcon, ListBulletIcon, TrashIcon, UserGroupIcon } from '@heroicons/react/24/outline'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CategoryBar, DonutChart, Flex, Grid, Legend, Metric, Text, Title } from "@tremor/react";
import { audioChat, getMeetingTranscript, reprocessMeeting, updateSpeakers } from '../../Requests/MeetingRequests';
import { mapSentimentAndColors, sentimentTranslations } from '../../utils/mappings';
import { Tracker } from "@tremor/react";
import { calculateQuestionCost, formatDateTime, formatTime } from '../../utils/calcs';
import { useMyContext } from '../ContextProviders/CreditsContextProvider';
import ViewTranscription from './ViewTranscription';
import { useTranslation } from 'react-i18next';
import { ButtonParticleLoader } from "../ParticleLoader";
import IdentifyUsersModal from './IdentifyUsersModal';
import MeetingOptionsMenu from './MeetingOptionsMenu';
import DeleteMeetingBlocksModal from './DeleteMeetingBlocksModal';
import MeetingSection from './MeetingSection';
import MeetingPermissions from './MeetingPermissions';
import NoCreditsModal from '../Billing/NoCreditsModal';
import LegendErrorBoundary from './LegendErrorBoundary';
import { getDeterministicSpeakerColor } from '../../utils/speakerColors';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

/* DEPRECATED: old Meetings pipeline. The active analysis view is NewAnalysisHolder (/analysis/:uuid). */
function ExpandedMeetingCard({ meeting, updateMeeting, view_mode = "private" }) {
    const { t } = useTranslation()
    const [answer, setAnswer] = useState(null);
    const { credits, setCredits } = useMyContext();
    const [transcription, setTranscription] = useState(null)
    const [modal, setModal] = useState(null)
    const [isAsking, setIsAsking] = useState(false)
    const [speakerNames, setSpeakerNames] = useState(false)
    const [deletePopUp, setDeletePopUp] = useState(null)
    const [insufficientCredits, setInsufficientCredits] = useState(null)
    const [isReprocessing, setIsReprocessing] = useState(false)

    const hasProcessingFailure = view_mode !== "public"
        && meeting.status !== "Not processed"
        && (!meeting.overall_sentiment || !meeting.overall_participation || !meeting.text_summary || !meeting.bullets_summary)

    const handleReprocess = () => {
        setIsReprocessing(true)
        reprocessMeeting({ meeting_uuid: meeting.uuid }).then(() => {
            setIsReprocessing(false)
            if (updateMeeting) updateMeeting()
        }).catch((e) => {
            setIsReprocessing(false)
        })
    }


    const handleQuestion = (e) => {
        e.preventDefault()
        setIsAsking(true)
        let data = {
            "meeting_uuid": meeting.uuid,
            "prompt": e.target.question.value
        }
        audioChat(data).then((res) => {
            setAnswer(res.data)

            setCredits((credits - calculateQuestionCost(meeting.duration)))
            setIsAsking(false)
        }).catch((e) => {
            if (JSON.parse(e.request.response)["message"] == "Insufficient credits") {
                setInsufficientCredits(<NoCreditsModal
                    title={t('CreditsModal.Title')}
                    body={t('CreditsModal.Body')}
                    continue={t('CreditsModal.Continue')}
                    navLink="/pricing/credits"
                    handleClose={() => { setInsufficientCredits(null) }} />)
            }
            setIsAsking(false)
        })
    }

    const navigate = useNavigate()

    // Comprehensive 50-color palette for large speaker datasets (21+ speakers)
    const SpeakerColors = [
        // Primary vibrant colors (1-10)
        "emerald-500", "yellow-500", "purple-500", "red-500", "lime-500",
        "blue-500", "pink-500", "indigo-500", "orange-500", "teal-500",
        
        // Secondary bright colors (11-20)
        "cyan-500", "violet-500", "rose-500", "amber-500", "green-500",
        "sky-500", "fuchsia-500", "slate-600", "stone-600", "neutral-600",
        
        // Tertiary distinct colors (21-30)
        "emerald-600", "yellow-600", "purple-600", "red-600", "lime-600",
        "blue-600", "pink-600", "indigo-600", "orange-600", "teal-600",
        
        // Darker variations (31-40)
        "emerald-700", "yellow-700", "purple-700", "red-700", "lime-700",
        "blue-700", "pink-700", "indigo-700", "orange-700", "teal-700",
        
        // Final backup colors (41-50)
        "gray-500", "gray-600", "gray-700", "zinc-500", "zinc-600",
        "slate-500", "stone-500", "neutral-500", "black", "gray-800"
    ];

    const getSpeakerColorRaw = (speaker) => {
        try {
            console.log(`🎨 getSpeakerColorRaw - Processing speaker: "${speaker}"`);
            
            // Use deterministic color assignment for consistency across components
            const selectedColor = getDeterministicSpeakerColor(speaker);
            
            console.log(`🎨 getSpeakerColorRaw - Speaker "${speaker}" → deterministic color: ${selectedColor}`);
            
            return selectedColor;
        } catch (error) {
            console.error(`🎨 getSpeakerColorRaw - Error processing speaker "${speaker}":`, error);
            return 'gray-500'; // Safe fallback
        }
    };

    // Function to get color for a speaker with bounds checking
    const getSpeakerColor = (letter) => {
        if (!letter || typeof letter !== 'string') {
            console.error('🎨 getSpeakerColor - Invalid letter:', letter);
            return SpeakerColors[0]; // Default to first color
        }

        // Get the index based on the ASCII code of the letter
        const index = letter.charCodeAt(0) - 'A'.charCodeAt(0);
        
        // Validate index is within reasonable bounds (A-Z = 0-25)
        if (index < 0 || index > 25) {
            console.warn(`🎨 getSpeakerColor - Letter "${letter}" outside A-Z range, index: ${index}`);
        }
        
        const colorIndex = index % SpeakerColors.length;
        const selectedColor = SpeakerColors[colorIndex];
        
        console.log(`🎨 Letter "${letter}" → index: ${index} → colorIndex: ${colorIndex} → color: ${selectedColor}`);
        console.log(`🎨 Color palette size: ${SpeakerColors.length}, Speaker index: ${index}, Selected index: ${colorIndex}`);
        
        return selectedColor;
    };

    const getLegendData = (sentimentData) => {
        console.log('🔧 GETLEGENDDATA FUNCTION - Called with data:', sentimentData);
        console.log('🔧 GETLEGENDDATA - Data type:', typeof sentimentData);
        console.log('🔧 GETLEGENDDATA - Data length:', sentimentData?.length);
        
        // Add validation to prevent Chart.js Legend crashes with undefined data
        if (!sentimentData || typeof sentimentData !== 'string') {
            console.error('🔧 GETLEGENDDATA FAIL - Invalid sentimentData:', sentimentData);
            return [];
        }
        
        let parsedData;
        try {
            parsedData = JSON.parse(sentimentData);
            console.log('🔧 GETLEGENDDATA - Parsed data:', parsedData);
            console.log('🔧 GETLEGENDDATA - Parsed data type:', typeof parsedData);
        } catch (error) {
            console.error('🔧 GETLEGENDDATA FAIL - JSON parse error:', error);
            return [];
        }
        
        if (!parsedData || typeof parsedData !== 'object') {
            console.error('🔧 GETLEGENDDATA FAIL - Invalid parsed data:', parsedData);
            return [];
        }

        console.log('🔧 GETLEGENDDATA - Object keys:', Object.keys(parsedData));
        console.log('🔧 GETLEGENDDATA - Total speakers to process:', Object.keys(parsedData).length);

        // Extract the speaker letters with safe validation
        const letters = [];
        Object.keys(parsedData).forEach((speaker, index) => {
            console.log(`🔧 GETLEGENDDATA - Processing speaker #${index + 1}:`, speaker);
            
            if (!speaker || typeof speaker !== 'string') {
                console.error(`🔧 GETLEGENDDATA FAIL - Invalid speaker key #${index + 1}:`, speaker);
                return; // Skip this speaker
            }
            
            try {
                // Extract the letter (e.g., 'A', 'B', etc.) from the speaker identifier (e.g., 'Speaker A')
                const parts = speaker.includes('Speaker ') ? speaker.split(' ')[1] : speaker;
                console.log(`🔧 GETLEGENDDATA - Speaker "${speaker}" → parts:`, parts);
                
                // Add validation for parts before calling split
                if (!parts || typeof parts !== 'string') {
                    console.error(`🔧 GETLEGENDDATA FAIL - Invalid parts for speaker "${speaker}":`, parts);
                    return; // Skip this speaker
                }
                
                const letter = parts.split('-')[0];
                console.log(`🔧 GETLEGENDDATA - Parts "${parts}" → letter:`, letter);
                
                // Validate letter before adding to array
                if (!letter || typeof letter !== 'string' || letter.trim() === '') {
                    console.error(`🔧 GETLEGENDDATA FAIL - Invalid letter for speaker "${speaker}":`, letter);
                    return; // Skip this speaker
                }

                // If the letter is not already in the array, add it
                if (!letters.includes(letter)) {
                    letters.push(letter);
                    console.log(`🔧 GETLEGENDDATA - Added letter "${letter}" to array. Current letters:`, letters);
                } else {
                    console.log(`🔧 GETLEGENDDATA - Letter "${letter}" already exists in array`);
                }
            } catch (error) {
                console.error(`🔧 GETLEGENDDATA FAIL - Error processing speaker "${speaker}":`, error);
                console.error(`🔧 GETLEGENDDATA - Error stack:`, error.stack);
                return; // Skip this speaker
            }
        });
        console.log('🔧 GETLEGENDDATA - Final letters array:', letters);
        console.log(`🔧 GETLEGENDDATA - Color availability: ${SpeakerColors.length} colors for ${letters.length} speakers`);
        
        if (letters.length > SpeakerColors.length) {
            console.warn(`⚠️ GETLEGENDDATA WARNING - Need ${letters.length} colors but only have ${SpeakerColors.length}. Colors will repeat.`);
        } else {
            console.log(`✅ GETLEGENDDATA - Sufficient colors available: ${SpeakerColors.length} >= ${letters.length}`);
        }
        
        console.log('🔧 GETLEGENDDATA - Creating final legend items...');
        
        const legendItems = letters.map((letter, index) => {
            try {
                const color = getSpeakerColor(letter);
                const item = {
                    label: `Speaker ${letter}`,
                    color: color
                };
                console.log(`🔧 GETLEGENDDATA - Legend item #${index + 1}:`, item);
                return item;
            } catch (error) {
                console.error(`🔧 GETLEGENDDATA FAIL - Error creating legend item for letter "${letter}":`, error);
                return {
                    label: `Speaker ${letter}`,
                    color: 'gray-500'
                };
            }
        });
        
        console.log('🔧 GETLEGENDDATA - Final legend items:', legendItems);
        console.log('🔧 GETLEGENDDATA - Total items created:', legendItems.length);
        
        return legendItems;
    };



    useEffect(() => {
        let names = null
        if (meeting.speaker_names) {
            names = JSON.parse(meeting.speaker_names)
            setSpeakerNames(names)
        }
        getMeetingTranscript(meeting.uuid).then((res) => {
            const lines = res.data.split("\n").map((line) => {
                const parts = line.split(":");
                const timeAndSpeaker = parts[0].trim(); // Trim any leading/trailing whitespace
                const speakerMatch = timeAndSpeaker.match(/(Speaker\s+[A-Z])$/); // Regex to match 'Speaker <letter>' at the end of the string
                const speakerKey = speakerMatch ? speakerMatch[0] : ""; // Extract the speaker key if it exists
                const time = speakerKey ? timeAndSpeaker.replace(speakerKey, "").trim() : timeAndSpeaker; // Extract the time part

                // Replace speakerKey with the value from speakerNames if it exists
                let speakerName = speakerKey
                if (names) {
                    speakerName = names[speakerKey] ? names[speakerKey] : speakerKey;
                }

                const final_line = {
                    staticPart: `${time} ${speakerName}:`,
                    editablePart: parts.slice(1).join(":").trim() || "", // Join back the remaining parts if there was a ':' in the editable part
                };
                if (final_line?.editablePart != "") {
                    return final_line
                } else {
                    return
                }
            });
            const filteredLines = lines.filter(line => line !== undefined);

            if (!filteredLines.every(element => element === undefined)) {
                setTranscription(filteredLines);
            } else {
                setTranscription(null)
            }
        });

    }, [meeting])



    const handleSaveNames = (names) => {
        let data = { "speaker_names": JSON.stringify(names), "meeting_uuid": meeting.uuid }
        updateSpeakers(data).then((res) => {
            window.location.reload(false)
        }).catch((e) => {

        })
    }


    const handleEditNames = () => {
        setModal(<IdentifyUsersModal handleClose={() => { setModal(null) }} speakerNames={speakerNames} handleSaveNames={handleSaveNames} />)
    }

    const handleDeleteData = () => {
        setModal(<DeleteMeetingBlocksModal handleClose={() => { setModal(null) }} meeting={meeting} setDeletePopUp={setDeletePopUp} updateMeeting={updateMeeting} />)

    }

    const handleVisibilitySettings = () => {
        setModal(<MeetingPermissions handleClose={() => { setModal(null) }} meeting={meeting} setDeletePopUp={setDeletePopUp} updateMeeting={updateMeeting} />)
    }


    return (
        <div className='shadow-2xl px-4 py-4 w-full bg-white rounded-2xl'>
            <div className='flex justify-between flex-col md:flex-row'>
                <div className='flex items-center justify-start mb-4 ml-1'>
                    <CircleStackIcon className='w-9 h-9' />
                    <div className=' pl-4'>
                        <div className='text-base font-semibold  text-gray-900'>
                            {meeting.name}
                        </div>
                        <div className='text-sm text-gray-500'>{formatDateTime(meeting.created_at)}</div>
                    </div>

                </div>
                <div className='flex flex-col md:flex-row gap-4 justify-between items-center print:hidden mb-4 md:mb-0 '>

                    {/*speakerNames && <button className='text-sm border-2 border-black  rounded-xl h-fit px-4 py-2 shadow-lg'
                        onClick={() => {
                            setModal(<IdentifyUsersModal handleClose={() => { setModal(null) }} speakerNames={speakerNames} handleSaveNames={handleSaveNames} />)
                        }}
                    >
                        <div className='flex justify-between gap-2 items-center'>
                            <UserGroupIcon className=' w-4 h-4' />
                            {t('TranscriptionModal.UpdateNamesTitle')}
                        </div>
                    </button>
                    */}

                    {transcription && <button className='text-sm border-2 border-black bg-black text-white rounded-xl h-fit px-4 py-2 shadow-lg'
                        onClick={() => {
                            setModal(<ViewTranscription handleClose={() => { setModal(null) }} transcription={transcription} setTranscription={setTranscription} meeting={meeting} view_mode={view_mode} />)
                        }}
                    >
                        <div className='flex justify-between gap-2 items-center'>
                            <EyeIcon className=' w-4 h-4' />
                            {t('ExpandedMeedingCard.ViewTranscriptionButton')}
                        </div>
                    </button>
                    }
                    {!(view_mode == "public") && <div className="py-1">
                        <button
                            type='button'
                            onClick={() => handleDeleteData()}
                            href="#"
                            className={'group flex items-center px-4 py-2 text-sm w-full'}
                        >
                            <TrashIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                            {t('ExpandedMeetingMenu.DeleteItemsLabel')}
                        </button>
                    </div>}

                    {!(view_mode == "public") && <MeetingOptionsMenu meeting={meeting} handleEditNames={handleEditNames} handleDeleteData={handleDeleteData} handleVisibilitySettings={handleVisibilitySettings} />}


                    {/*<button className='text-sm bg-black text-white rounded-xl h-fit px-4 py-2 border-2 border-black shadow-lg'

                >{t('ExpandedMeedingCard.EditButton')}</button>*/}
                </div>
            </div>

            {hasProcessingFailure &&
                <div className='w-full flex flex-col md:flex-row items-start md:items-center justify-between gap-3 bg-red-600 text-white rounded-xl px-4 py-3 mb-4 shadow-lg print:hidden'>
                    <div className='flex items-start gap-2 text-sm'>
                        <ExclamationTriangleIcon className='w-5 h-5 flex-shrink-0' />
                        {t('ExpandedMeedingCard.ReprocessMessage')}
                    </div>
                    <button
                        type='button'
                        onClick={handleReprocess}
                        disabled={isReprocessing}
                        className='text-sm font-semibold bg-white text-red-700 rounded-xl px-4 py-2 shadow whitespace-nowrap hover:bg-red-50 disabled:opacity-50'
                    >
                        {isReprocessing ? t('ExpandedMeedingCard.ReprocessingLabel') : t('ExpandedMeedingCard.ReprocessActionButton')}
                    </button>
                </div>
            }

            <Grid numItemsSm={2} numItemsLg={2} className="gap-6">
                <Card className='max-h-96'>
                    {meeting.overall_sentiment ?
                        <div className="max-w-lg">
                            <Flex alignItems="start">
                                <Text>{t('ExpandedMeedingCard.OverallSentimentLavel')}</Text>

                            </Flex>
                            <Flex className="space-x-3 truncate" justifyContent="start" alignItems="baseline">
                                <Metric></Metric>
                            </Flex>

                            <DonutChart
                                className="mt-6 max-h-32"
                                data={
                                    Object.entries(JSON.parse(meeting.overall_sentiment)).map(([key, value]) => {
                                        return { name: sentimentTranslations(key, t('Language')), value: parseFloat(value) };
                                    })
                                }
                                category="value"
                                index="name"
                                colors={
                                    Object.entries(JSON.parse(meeting.overall_sentiment)).map(([key, value]) => (
                                        mapSentimentAndColors(key) ? mapSentimentAndColors(key) : "black"
                                    ))
                                }
                                showAnimation={true}
                                variant='pie'
                            />
                            <div className='w-full flex justify-center items-center'>
                                {(() => {
                                    console.log('🔍 LEGEND DEBUG #1 - Overall Sentiment Legend');
                                    console.log('📊 Raw overall_sentiment data:', meeting.overall_sentiment);
                                    
                                    // Aggressive defensive rendering - multiple validation layers
                                    if (!meeting) {
                                        console.error('❌ LEGEND #1 FAIL - No meeting object');
                                        return <div className="text-red-500 italic">Legend #1 Failed: No meeting data</div>;
                                    }
                                    
                                    if (meeting.overall_sentiment === null || meeting.overall_sentiment === undefined) {
                                        console.error('❌ LEGEND #1 FAIL - overall_sentiment is null/undefined');
                                        return <div className="text-red-500 italic">Legend #1 Failed: No sentiment data available</div>;
                                    }
                                    
                                    if (typeof meeting.overall_sentiment !== 'string') {
                                        console.error('❌ LEGEND #1 FAIL - overall_sentiment is not a string:', typeof meeting.overall_sentiment);
                                        return <div className="text-red-500 italic">Legend #1 Failed: Invalid sentiment data type</div>;
                                    }
                                    
                                    if (meeting.overall_sentiment.trim() === '') {
                                        console.error('❌ LEGEND #1 FAIL - overall_sentiment is empty string');
                                        return <div className="text-red-500 italic">Legend #1 Failed: Empty sentiment data</div>;
                                    }
                                    
                                    let sentimentData;
                                    try {
                                        sentimentData = JSON.parse(meeting.overall_sentiment);
                                        console.log('✅ Parsed overall_sentiment:', sentimentData);
                                    } catch (error) {
                                        console.error('❌ LEGEND #1 FAIL - JSON parse error:', error);
                                        return <div className="text-red-500 italic">Legend #1 Failed: Invalid JSON data</div>;
                                    }
                                    
                                    if (!sentimentData) {
                                        console.error('❌ LEGEND #1 FAIL - Parsed sentiment data is null/undefined');
                                        return <div className="text-red-500 italic">Legend #1 Failed: Null parsed data</div>;
                                    }
                                    
                                    if (typeof sentimentData !== 'object') {
                                        console.error('❌ LEGEND #1 FAIL - Invalid data structure:', typeof sentimentData);
                                        return <div className="text-red-500 italic">Legend #1 Failed: Non-object data structure</div>;
                                    }
                                    
                                    if (Array.isArray(sentimentData)) {
                                        console.error('❌ LEGEND #1 FAIL - Sentiment data is array, expected object');
                                        return <div className="text-red-500 italic">Legend #1 Failed: Array instead of object</div>;
                                    }

                                    const entries = Object.entries(sentimentData);
                                    console.log('📋 Overall sentiment entries:', entries);
                                    
                                    if (!Array.isArray(entries)) {
                                        console.error('❌ LEGEND #1 FAIL - Object.entries did not return array');
                                        return <div className="text-red-500 italic">Legend #1 Failed: Invalid entries processing</div>;
                                    }
                                    
                                    if (entries.length === 0) {
                                        console.error('❌ LEGEND #1 FAIL - No entries found in sentiment data');
                                        return <div className="text-red-500 italic">Legend #1 Failed: No sentiment entries</div>;
                                    }
                                    
                                    let categories, colors;
                                    try {
                                        categories = entries
                                            .filter(([key, value]) => {
                                                // Aggressive validation of each entry
                                                if (typeof key !== 'string') {
                                                    console.error('❌ LEGEND #1 FAIL - Invalid key type:', typeof key, key);
                                                    return false;
                                                }
                                                if (key.trim() === '') {
                                                    console.error('❌ LEGEND #1 FAIL - Empty key:', key);
                                                    return false;
                                                }
                                                if (value === null || value === undefined) {
                                                    console.error('❌ LEGEND #1 FAIL - Invalid value:', value);
                                                    return false;
                                                }
                                                return true;
                                            })
                                            .map(([key, value]) => {
                                                try {
                                                    const translation = sentimentTranslations(key, t('Language'));
                                                    if (!translation || typeof translation !== 'string') {
                                                        console.warn('⚠️ LEGEND #1 WARN - No translation for:', key, 'using key as fallback');
                                                        return `${key} (${value}%)`;
                                                    }
                                                    const category = translation + " (" + value + "%)";
                                                    console.log(`🏷️ Category for ${key}:`, category);
                                                    return category;
                                                } catch (error) {
                                                    console.error('❌ LEGEND #1 FAIL - Category creation error for key:', key, error);
                                                    return `${key} (${value}%)`;
                                                }
                                            });

                                        colors = entries
                                            .filter(([key, value]) => typeof key === 'string' && key.trim() !== '' && value !== null && value !== undefined)
                                            .map(([key, value]) => {
                                                try {
                                                    const color = mapSentimentAndColors(key);
                                                    if (!color || typeof color !== 'string') {
                                                        console.warn('⚠️ LEGEND #1 WARN - No color mapping for:', key, 'using black fallback');
                                                        return "black";
                                                    }
                                                    console.log(`🎨 Color for ${key}:`, color);
                                                    return color;
                                                } catch (error) {
                                                    console.error('❌ LEGEND #1 FAIL - Color mapping error for key:', key, error);
                                                    return "black";
                                                }
                                            });
                                    } catch (error) {
                                        console.error('❌ LEGEND #1 FAIL - Categories/colors processing error:', error);
                                        return <div className="text-red-500 italic">Legend #1 Failed: Data processing error</div>;
                                    }
                                    
                                    // Final validation before rendering
                                    if (!Array.isArray(categories) || categories.length === 0) {
                                        console.error('❌ LEGEND #1 FAIL - Invalid categories array:', categories);
                                        return <div className="text-red-500 italic">Legend #1 Failed: No valid categories</div>;
                                    }
                                    
                                    if (!Array.isArray(colors) || colors.length === 0) {
                                        console.error('❌ LEGEND #1 FAIL - Invalid colors array:', colors);
                                        return <div className="text-red-500 italic">Legend #1 Failed: No valid colors</div>;
                                    }
                                    
                                    if (categories.length !== colors.length) {
                                        console.error('❌ LEGEND #1 FAIL - Categories/colors length mismatch:', categories.length, colors.length);
                                        return <div className="text-red-500 italic">Legend #1 Failed: Data length mismatch</div>;
                                    }
                                    
                                    // Check for undefined/null values in arrays
                                    const hasUndefinedCategories = categories.some(cat => cat === undefined || cat === null || cat === '');
                                    const hasUndefinedColors = colors.some(color => color === undefined || color === null || color === '');
                                    
                                    if (hasUndefinedCategories) {
                                        console.error('❌ LEGEND #1 FAIL - Undefined values in categories:', categories);
                                        return <div className="text-red-500 italic">Legend #1 Failed: Invalid category values</div>;
                                    }
                                    
                                    if (hasUndefinedColors) {
                                        console.error('❌ LEGEND #1 FAIL - Undefined values in colors:', colors);
                                        return <div className="text-red-500 italic">Legend #1 Failed: Invalid color values</div>;
                                    }
                                    
                                    console.log('📊 LEGEND #1 Final categories:', categories);
                                    console.log('🎨 LEGEND #1 Final colors:', colors);
                                    console.log('✅ LEGEND #1 - Rendering overall sentiment Legend successfully');

                                    return <LegendErrorBoundary 
                                        legendId="legend-1-overall-sentiment"
                                        legendDescription="Overall Sentiment Legend"
                                    >
                                        <Legend
                                            className="mt-3"
                                            categories={categories}
                                            colors={colors}
                                        />
                                    </LegendErrorBoundary>;
                                })()}
                            </div>
                        </div> : <div className='min-w-full h-32 bg-gray-200 animate-pulse'></div>}
                </Card>
                <Card className='flex flex-col h-full justify-between max-h-96'>
                    {meeting.overall_participation ?
                        <>
                            <div>
                                <Flex alignItems="start">
                                    <Text>{t('ExpandedMeedingCard.TimeSpokenLabel')}</Text>
                                </Flex>
                                <Flex className="space-x-3 truncate" justifyContent="start" alignItems="baseline">
                                    <Metric>{formatTime(meeting.duration)}</Metric>
                                </Flex>
                            </div>

                            <div>

                                <DonutChart
                                    className="mt-6 max-h-32"
                                    data={
                                        Object.entries(JSON.parse(meeting.overall_participation)).map(([key, value]) => {
                                            return { name: key, value: parseFloat(value) };
                                        })
                                    }
                                    category="value"
                                    index="name"
                                    colors={["emerald-500", "yellow-500", "purple-500", "red-500", "lime-500", "zinc-500", "gray-500", "indigo-500", "orange-500", "black", "teal-500", "cyan-500"]}
                                    showAnimation={true}
                                    showLabel={false}
                                />

                            </div>





                            <div className='w-full flex justify-center items-center'>
                                {(() => {
                                    console.log('🔍 LEGEND DEBUG #2 - Overall Participation Legend');
                                    console.log('📊 Raw overall_participation data:', meeting.overall_participation);
                                    
                                    let participationData;
                                    try {
                                        participationData = JSON.parse(meeting.overall_participation);
                                        console.log('✅ Parsed overall_participation:', participationData);
                                    } catch (error) {
                                        console.error('❌ LEGEND #2 FAIL - JSON parse error:', error);
                                        return <div className="text-red-500 italic">Legend #2 Failed: Invalid participation data</div>;
                                    }
                                    
                                    if (!participationData || typeof participationData !== 'object') {
                                        console.error('❌ LEGEND #2 FAIL - Invalid data structure:', participationData);
                                        return <div className="text-red-500 italic">Legend #2 Failed: Invalid data structure</div>;
                                    }

                                    const entries = Object.entries(participationData);
                                    console.log('📋 Overall participation entries:', entries);
                                    console.log('📊 Total participation speakers:', entries.length);
                                    
                                    const categories = entries.map(([key, value]) => {
                                        const category = (key) + " (" + value + "%)";
                                        console.log(`🏷️ Category for ${key}:`, category);
                                        return category;
                                    });
                                    
                                    console.log('🎨 Using SpeakerColors palette:', SpeakerColors);
                                    console.log(`🎨 Colors available: ${SpeakerColors.length}, Categories needed: ${categories.length}`);
                                    
                                    // Ensure we have enough colors for all categories
                                    if (categories.length > SpeakerColors.length) {
                                        console.warn(`⚠️ LEGEND #2 WARNING - Need ${categories.length} colors but only have ${SpeakerColors.length}. Some speakers will share colors.`);
                                    }
                                    
                                    console.log('📊 LEGEND #2 Final categories:', categories);
                                    console.log('🎨 LEGEND #2 Final colors:', SpeakerColors);
                                    console.log('✅ LEGEND #2 - Rendering overall participation Legend successfully');

                                    return <LegendErrorBoundary 
                                        legendId="legend-2-overall-participation"
                                        legendDescription="Overall Participation Legend"
                                    >
                                        <Legend
                                            className="mt-3"
                                            categories={categories}
                                            colors={SpeakerColors}
                                        />
                                    </LegendErrorBoundary>;
                                })()}
                            </div>
                        </>
                        : <div className='min-w-full h-32 bg-gray-200 animate-pulse'></div>}
                </Card>

            </Grid>
            <MeetingSection
                icon={Bars2Icon}
                title={t('ExpandedMeedingCard.SummaryLabel')}
                content={meeting.text_summary}
                meeting_uuid={meeting.uuid}
                lines={8}
                content_name="text_summary"
                updateMeeting={updateMeeting}
                view_mode={view_mode}
            />

            <MeetingSection
                icon={ListBulletIcon}
                title={t('ExpandedMeedingCard.SummaryByPointsLabel')}
                content={meeting.bullets_summary}
                meeting_uuid={meeting.uuid}
                lines={20}
                content_name="bullets_summary"
                updateMeeting={updateMeeting}
                view_mode={view_mode}
            />

            <MeetingSection
                icon={CheckIcon}
                title={t('ExpandedMeedingCard.TasksLavel')}
                content={meeting.tasks}
                meeting_uuid={meeting.uuid}
                lines={12}
                content_name="tasks"
                updateMeeting={updateMeeting}
                view_mode={view_mode}
            />

            {/** */}
            <Card className='flex flex-col h-full justify-between my-4'>
                <Flex className="space-x-3 truncate mb-4" justifyContent="start" alignItems="baseline">
                    <Metric>{t('ExpandedMeedingCard.MeetingProgressionLabel')}</Metric>
                </Flex>

                {meeting.time_based_participation ?
                    <>
                        <div>
                            <Flex alignItems="start">
                                <Text>{t('ExpandedMeedingCard.SpeakerOverTimeLabel')}</Text>
                            </Flex>

                        </div>

                        <div>
                            {(() => {
                                // Safe parsing for time_based_participation to prevent Chart.js Legend crashes
                                let participationData;
                                try {
                                    participationData = JSON.parse(meeting.time_based_participation);
                                } catch (error) {
                                    console.warn('Failed to parse time_based_participation:', error);
                                    return <div className="text-gray-500 italic">Invalid participation data</div>;
                                }
                                
                                if (!participationData || typeof participationData !== 'object') {
                                    console.warn('Invalid participation data structure:', participationData);
                                    return <div className="text-gray-500 italic">Invalid participation data</div>;
                                }

                                const entries = Object.entries(participationData);
                                if (entries.length === 0) {
                                    return <div className="text-gray-500 italic">No participation data available</div>;
                                }

                                return <CategoryBar className="mt-4"
                                    values={
                                        entries.map(([key, value]) => {
                                            const numValue = parseFloat(value);
                                            return isNaN(numValue) ? 0 : numValue / meeting.duration;
                                        })
                                    }
                                    colors={
                                        entries.map(([key, value]) => {
                                            try {
                                                return getSpeakerColorRaw(key);
                                            } catch (error) {
                                                console.warn('Failed to get color for speaker:', key, error);
                                                return "gray-500"; // fallback color
                                            }
                                        })
                                    }
                                    showLabels={false}
                                />;
                            })()}

                        </div>
                        <div className='w-full flex justify-center items-center'>
                            {(() => {
                                console.log('🔍 LEGEND DEBUG #3 - Time Based Participation Legend (using getLegendData)');
                                console.log('📊 Raw time_based_participation data:', meeting.time_based_participation);
                                
                                try {
                                    const legendData1 = getLegendData(meeting.time_based_participation);
                                    console.log('✅ getLegendData result #1:', legendData1);
                                    
                                    const categories = legendData1.map(item => {
                                        console.log(`🏷️ Category item:`, item);
                                        if (!item || typeof item !== 'object' || !item.label) {
                                            console.error('❌ LEGEND #3 FAIL - Invalid category item:', item);
                                            return 'Invalid Category';
                                        }
                                        return item.label;
                                    });
                                    
                                    const legendData2 = getLegendData(meeting.time_based_participation);
                                    console.log('✅ getLegendData result #2:', legendData2);
                                    
                                    const colors = legendData2.map(item => {
                                        console.log(`🎨 Color item:`, item);
                                        if (!item || typeof item !== 'object' || !item.color) {
                                            console.error('❌ LEGEND #3 FAIL - Invalid color item:', item);
                                            return 'gray-500';
                                        }
                                        return item.color;
                                    });
                                    
                                    console.log('📊 LEGEND #3 Final categories:', categories);
                                    console.log('🎨 LEGEND #3 Final colors:', colors);
                                    console.log(`📊 Categories length: ${categories.length}, Colors length: ${colors.length}`);
                                    console.log('✅ LEGEND #3 - Rendering time based participation Legend successfully');

                                    return <LegendErrorBoundary 
                                        legendId="legend-3-time-based-participation"
                                        legendDescription="Time Based Participation Legend"
                                    >
                                        <Legend
                                            className="mt-3"
                                            categories={categories}
                                            colors={colors}
                                        />
                                    </LegendErrorBoundary>;
                                } catch (error) {
                                    console.error('❌ LEGEND #3 FAIL - Unexpected error:', error);
                                    console.error('❌ LEGEND #3 ERROR STACK:', error.stack);
                                    return <div className="text-red-500 italic">Legend #3 Failed: {error.message}</div>;
                                }
                            })()}
                        </div>

                    </>
                    : <div className='min-w-full h-32 bg-gray-200 animate-pulse'></div>}


                {meeting.time_based_sentiment ?
                    <>

                        <div>
                            <Flex alignItems="start">
                                <Text>{t('ExpandedMeedingCard.DominantSentimentOverTime')}</Text>
                            </Flex>
                        </div>

                        <div>
                            {(() => {
                                // Safe parsing for time_based_sentiment to prevent Chart.js Legend crashes
                                let sentimentData;
                                try {
                                    sentimentData = JSON.parse(meeting.time_based_sentiment);
                                } catch (error) {
                                    console.warn('Failed to parse time_based_sentiment:', error);
                                    return <div className="text-gray-500 italic">Invalid sentiment data</div>;
                                }
                                
                                if (!sentimentData || typeof sentimentData !== 'object') {
                                    console.warn('Invalid sentiment data structure:', sentimentData);
                                    return <div className="text-gray-500 italic">Invalid sentiment data</div>;
                                }

                                const entries = Object.entries(sentimentData);
                                if (entries.length === 0) {
                                    return <div className="text-gray-500 italic">No sentiment data available</div>;
                                }

                                return <Tracker className="mt-4"
                                    data={
                                        entries.map(([key, value]) => {
                                            try {
                                                return { 
                                                    tooltip: sentimentTranslations(value, t('Language')) || 'Unknown', 
                                                    color: mapSentimentAndColors(value) || 'gray'
                                                };
                                            } catch (error) {
                                                console.warn('Error processing sentiment entry:', key, value, error);
                                                return { tooltip: 'Unknown', color: 'gray' };
                                            }
                                        })
                                    }
                                />;
                            })()}

                        </div>
                        {meeting.time_based_sentiment && <div className='w-full flex justify-center items-center'>
                            {(() => {
                                console.log('🔍 LEGEND DEBUG #4 - Time Based Sentiment Legend');
                                console.log('📊 Raw time_based_sentiment data:', meeting.time_based_sentiment);
                                
                                // Safe parsing for sentiment legend
                                let sentimentData;
                                try {
                                    sentimentData = JSON.parse(meeting.time_based_sentiment);
                                    console.log('✅ Parsed time_based_sentiment:', sentimentData);
                                } catch (error) {
                                    console.error('❌ LEGEND #4 FAIL - JSON parse error:', error);
                                    return <div className="text-red-500 italic">Legend #4 Failed: Invalid sentiment data</div>;
                                }
                                
                                if (!sentimentData || typeof sentimentData !== 'object') {
                                    console.error('❌ LEGEND #4 FAIL - Invalid data structure:', sentimentData);
                                    return <div className="text-red-500 italic">Legend #4 Failed: Invalid data structure</div>;
                                }

                                const values = Object.values(sentimentData);
                                console.log('📋 Sentiment values:', values);
                                if (values.length === 0) {
                                    console.error('❌ LEGEND #4 FAIL - No values found');
                                    return <div className="text-red-500 italic">Legend #4 Failed: No sentiment data available</div>;
                                }

                                const uniqueValues = [...new Set(values)].filter(value => value != null);
                                console.log('📋 Unique sentiment values:', uniqueValues);
                                if (uniqueValues.length === 0) {
                                    console.error('❌ LEGEND #4 FAIL - No valid unique values');
                                    return <div className="text-red-500 italic">Legend #4 Failed: No valid sentiment data</div>;
                                }

                                const categories = uniqueValues.map(value => {
                                    try {
                                        const category = sentimentTranslations(value, t('Language')) || 'Unknown';
                                        console.log(`🏷️ Sentiment category for ${value}:`, category);
                                        return category;
                                    } catch (error) {
                                        console.error('❌ LEGEND #4 FAIL - Error translating sentiment:', value, error);
                                        return 'Unknown';
                                    }
                                });
                                
                                const colors = uniqueValues.map(value => {
                                    try {
                                        const color = mapSentimentAndColors(value) || "gray";
                                        console.log(`🎨 Sentiment color for ${value}:`, color);
                                        return color;
                                    } catch (error) {
                                        console.error('❌ LEGEND #4 FAIL - Error mapping sentiment color:', value, error);
                                        return "gray";
                                    }
                                });
                                
                                console.log('📊 LEGEND #4 Final categories:', categories);
                                console.log('🎨 LEGEND #4 Final colors:', colors);
                                console.log(`📊 Categories length: ${categories.length}, Colors length: ${colors.length}`);
                                console.log('✅ LEGEND #4 - Rendering time based sentiment Legend successfully');

                                return <LegendErrorBoundary 
                                    legendId="legend-4-time-based-sentiment"
                                    legendDescription="Time Based Sentiment Legend"
                                >
                                    <Legend
                                        className="mt-3"
                                        categories={categories}
                                        colors={colors}
                                    />
                                </LegendErrorBoundary>;
                            })()}
                        </div>}

                    </>
                    : <div className='min-w-full h-32 bg-gray-200 animate-pulse'></div>}
            </Card>

            {/*meeting.people_based_sentiment &&
                <Card className='flex w-full justify-evenly'>
                    {JSON.parse(meeting.people_based_sentiment).map((person_sentiment) => {
                        return (Object.entries(person_sentiment).map(([speaker, attributes]) => {
                            return (<div className='w-full flex flex-col justify-center'>
                                <div className='text-center'>{speaker}</div>
                                <DonutChart
                                    className="mt-6 max-h-32"
                                    data={
                                        Object.entries(attributes).map(([key, value]) => {
                                            { console.log(key, parseFloat(value.replace("%", ""))) }
                                            return { name: key, value: parseFloat(value.replace("%", "")) };
                                        })
                                    }
                                    category="value"
                                    index="name"
                                    colors={
                                        Object.entries(attributes).map(([key, value]) => (
                                            mapSentimentAndColors(key) ? mapSentimentAndColors(key) : "black"
                                        ))
                                    }
                                    showAnimation={true}
                                    variant='pie'
                                />
                                {<div className='w-full flex justify-center items-center'>
                                    <Legend
                                        className="mt-3"
                                        categories={
                                            Object.entries(attributes).map(([key, value]) => (
                                                (key)
                                            ))
                                        }
                                        colors={
                                            Object.entries(attributes).map(([key, value]) => (
                                                mapSentimentAndColors(key) ? mapSentimentAndColors(key) : "black"
                                            ))
                                        }
                                    />
                                </div>}
                            </div>)
                        }));
                    })}

                </Card>
                */}
            {view_mode != "public" &&
                <>
                    <div className='text-sm my-4 print:hidden'>
                        <Card>
                            <div className='flex gap-2 items-center text-lg mb-4'>
                                <ChatBubbleLeftIcon className='border border-black text-black bg-white rounded-md w-6 h-6 px-0.5 py-0.5'></ChatBubbleLeftIcon>
                                {t('ExpandedMeedingCard.ChatLabel')}
                            </div>
                            <form onSubmit={handleQuestion} className='flex flex-col md:flex-row gap-2 items-center'>
                                <input
                                    type="text"
                                    name="question"
                                    id="question"
                                    className="block  w-full text-center rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                    placeholder={t('ExpandedMeedingCard.ChatPlaceholder')}
                                >
                                </input>
                                <button className='bg-black text-white rounded-lg px-4 py-2 w-full md:w-auto'>
                                    {isAsking ? <ButtonParticleLoader /> :
                                        <div className='flex justify-between items-center gap-3'>
                                            <div>Ask</div>
                                            <div className='flex items-center gap-1'>
                                                <div className='font-bold'>{calculateQuestionCost(meeting.duration)}</div>
                                                <CpuChipIcon className='w-5 h-5' />
                                            </div>
                                        </div>}

                                </button>
                            </form>
                            {answer && <div className='mt-8'>  {t('ExpandedMeedingCard.AnswerLabel')}: <br />{answer.response}</div>}

                        </Card>
                    </div>
                    <div className='prints hidden print:block'>
                        <Card >
                            <div className='flex gap-2 items-center text-lg mb-4'>
                                <ChatBubbleLeftIcon className='border border-black text-black bg-white rounded-md w-6 h-6 px-0.5 py-0.5' />
                                {t('TranscriptionModal.TranscriptionLabel')}
                            </div>
                            <div >
                                {transcription?.map((line, index) => {
                                    if (line?.staticPart?.length > 1) {
                                        return <div className="text-base my-2">
                                            <div className="text-base mt-0">{line.staticPart.replace("-", "s-").replace("s]")} {line.editablePart}</div>
                                        </div>
                                    }
                                })}

                            </div>
                        </Card>
                    </div>
                </>
            }
            <div className='flex justify-center mt-4 print:hidden' onClick={() => { window.print() }}>
                <button className='bg-black text-white rounded-lg px-8 py-2'>
                    <div className='flex justify-between items-center gap-3'>
                        <div>  {t('ExpandedMeedingCard.PrintLabel')}</div>
                    </div>
                </button>
            </div>
            {modal}
            {deletePopUp}
            {insufficientCredits}
        </div>
    )
}

export default ExpandedMeetingCard
